/**
 * When an asset is created from an asset model, compatible parts and planned
 * maintenance/inspection work should mirror the model's templates (mock parity with backend).
 * Also seeds initial demo assets so lists match model sub-resources.
 */

import { mockAssets } from "@entities/asset/mocks";
import { mockMaintenances } from "@entities/maintenance/mocks";
import { mockInspections } from "@entities/inspection/mocks";
import { mockAssetParts } from "@entities/part/mocks";
import {
  DueTimingStatusConstants as MaintDueTiming,
  INTERVAL_MODES as MaintIntervalModes,
  INTERVAL_TYPES as MaintIntervalTypes,
  MAINTENANCE_TYPE,
  WorkflowStatusConstants as MaintWorkflow,
} from "@/features/maintenance/constants";
import {
  DueTimingStatusConstants as InspDueTiming,
  INTERVAL_MODES as InspIntervalModes,
  INTERVAL_TYPES as InspIntervalTypes,
  WorkflowStatusConstants as InspWorkflow,
} from "@/features/inspection/constants";
import type { Maintenance } from "@entities/maintenance/types";
import type { Inspection } from "@entities/inspection/types";
import type { AssetPart } from "@entities/part/types";
import {
  mockAssetModelCompatibleParts,
  mockAssetModelMaintenanceTemplates,
  type MockMaintenanceTemplate,
} from "../stores";

const DEFAULT_ACCOUNT_ID = "1";
const DEFAULT_ASSIGNEE_ID = "per-14";

function addDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

function mapMaintIntervalType(
  t: MockMaintenanceTemplate
): (typeof MaintIntervalTypes)[keyof typeof MaintIntervalTypes] {
  const raw = (t.intervalType ?? "").toLowerCase();
  if (raw === "years" || raw === "year") return MaintIntervalTypes.YEARLY;
  if (raw === "weeks" || raw === "week") return MaintIntervalTypes.WEEKLY;
  if (raw === "days" || raw === "day") return MaintIntervalTypes.DAILY;
  return MaintIntervalTypes.MONTHLY;
}

function mapMaintIntervalMode(
  t: MockMaintenanceTemplate
): (typeof MaintIntervalModes)[keyof typeof MaintIntervalModes] {
  const raw = (t.intervalMode ?? "").toLowerCase();
  if (raw === "relative" || raw === "operating_hours") return MaintIntervalModes.RELATIVE;
  return MaintIntervalModes.FIXED_DATE;
}

function mapInspIntervalType(
  t: MockMaintenanceTemplate
): (typeof InspIntervalTypes)[keyof typeof InspIntervalTypes] {
  const raw = (t.intervalType ?? "").toLowerCase();
  if (raw === "years" || raw === "year") return InspIntervalTypes.YEARLY;
  if (raw === "weeks" || raw === "week") return InspIntervalTypes.WEEKLY;
  if (raw === "days" || raw === "day") return InspIntervalTypes.DAILY;
  return InspIntervalTypes.MONTHLY;
}

function mapInspIntervalMode(
  t: MockMaintenanceTemplate
): (typeof InspIntervalModes)[keyof typeof InspIntervalModes] {
  const raw = (t.intervalMode ?? "").toLowerCase();
  if (raw === "relative" || raw === "operating_hours") return InspIntervalModes.RELATIVE;
  return InspIntervalModes.FIXED_DATE;
}

/**
 * Idempotently adds asset parts, maintenances, and inspections derived from the current
 * asset model templates and compatible-parts list (same arrays REST/GQL mock uses).
 */
export function applyModelDefaultsToNewAsset(
  assetId: string,
  assetModelId: string | null | undefined
): void {
  if (!assetModelId?.trim()) return;

  const now = new Date().toISOString();
  const scheduledDate = addDaysIso(30);

  const compatible = mockAssetModelCompatibleParts.filter((p) => p.assetModelId === assetModelId);
  for (const cp of compatible) {
    const linkId = `ap-modeltpl-${assetId}-${cp.partId}`;
    if (mockAssetParts.some((ap) => ap.id === linkId)) continue;
    const row: AssetPart = {
      id: linkId,
      asset_id: assetId,
      part_id: cp.partId,
      quantity: cp.referenceQuantity ?? 1,
      createdAt: now,
      updatedAt: now,
    };
    mockAssetParts.push(row);
  }

  const templates = mockAssetModelMaintenanceTemplates.filter(
    (t) => t.assetModelId === assetModelId && t.isActive
  );
  for (const tmpl of templates) {
    const kind = (tmpl.type ?? "maintenance").toLowerCase();
    if (kind === "inspection") {
      const inspId = `insp-modeltpl-${assetId}-${tmpl.id}`;
      if (mockInspections.some((i) => i.id === inspId)) continue;
      const row: Inspection = {
        id: inspId,
        accountId: DEFAULT_ACCOUNT_ID,
        assetId,
        assignedToId: DEFAULT_ASSIGNEE_ID,
        title: tmpl.title,
        type: MAINTENANCE_TYPE.INSPECTION,
        workflowStatus: InspWorkflow.OPEN,
        dueTimingStatus: InspDueTiming.UPCOMING,
        description: tmpl.description ?? undefined,
        scheduledDate,
        isExternal: Boolean(tmpl.isExternal),
        areTasksPaused: false,
        intervalMode: mapInspIntervalMode(tmpl),
        intervalType: mapInspIntervalType(tmpl),
        intervalNumber: tmpl.intervalNumber ?? 1,
        durationHours: tmpl.durationHours ?? undefined,
        durationMinutes: tmpl.durationMinutes ?? undefined,
        overdueDays: tmpl.overdueDays ?? undefined,
        createdAt: now,
        updatedAt: now,
      };
      mockInspections.push(row);
    } else {
      const mntId = `mnt-modeltpl-${assetId}-${tmpl.id}`;
      if (mockMaintenances.some((m) => m.id === mntId)) continue;
      const row: Maintenance = {
        id: mntId,
        accountId: DEFAULT_ACCOUNT_ID,
        assetId,
        assignedToId: DEFAULT_ASSIGNEE_ID,
        title: tmpl.title,
        type: MAINTENANCE_TYPE.MAINTENANCE,
        workflowStatus: MaintWorkflow.OPEN,
        dueTimingStatus: MaintDueTiming.UPCOMING,
        description: tmpl.description ?? undefined,
        scheduledDate,
        isExternal: Boolean(tmpl.isExternal),
        areTasksPaused: false,
        intervalMode: mapMaintIntervalMode(tmpl),
        intervalType: mapMaintIntervalType(tmpl),
        intervalNumber: tmpl.intervalNumber ?? 1,
        durationHours: tmpl.durationHours ?? undefined,
        durationMinutes: tmpl.durationMinutes ?? undefined,
        overdueDays: tmpl.overdueDays ?? undefined,
        upcomingDays: tmpl.upcomingDays ?? undefined,
        createdAt: now,
        updatedAt: now,
      };
      mockMaintenances.push(row);
    }
  }
}

/** One-time: align all seed assets with their model's compatible parts + templates. */
export function seedAssetDerivedDataFromModels(): void {
  for (const asset of mockAssets) {
    applyModelDefaultsToNewAsset(asset.id, asset.asset_model_id ?? undefined);
  }
}
